import { drawDocument, moduleFor } from '@/frontend/reports/drawDocument';
import { LoadedReportModule } from '@/frontend/reports/runtime';
import { CompiledReportBundle } from '@/reports/bundles/types';
import { RunTraceRequest } from '@/reports/runs';
import { InputsReportContext, RunReportContext } from '@/reports/types';

/**
 * What the preview shows, as text, for the studio's assistant: the draft's
 * verdict and findings over the run in the preview, then its document drawn
 * offscreen exactly as the Preview tab draws it and read back as an outline —
 * headings, paragraphs, tables row by row, figure captions. Whatever a block
 * prints in place of content it could not find is marked [unset: …].
 */

const OUTLINE_CHARS = 30_000;
const TABLE_ROWS = 25;
const BLOCK_TAGS = new Set(['ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DETAILS', 'DIV', 'DL', 'DT', 'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'TABLE', 'UL', 'svg']);
const HEADINGS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

const squash = (s: string) => s.replace(/\s+/g, ' ').trim();
const isBlock = (el: Element) => BLOCK_TAGS.has(el.tagName) || BLOCK_TAGS.has(el.tagName.toUpperCase());

/** An element's text on one line, `.unset` content marked, controls read by their value. */
function inline(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof Element)) return '';
  if (node.classList.contains('unset')) return ` [unset: ${squash(node.textContent ?? '')}] `;
  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) return ` [field: ${node.value || node.placeholder || 'empty'}] `;
  if (node instanceof HTMLSelectElement) return ` [choice: ${node.value || 'none'}] `;
  if (node.tagName === 'BR') return ' ';
  const parts = Array.from(node.childNodes).map(inline);
  return isBlock(node) ? ` ${parts.join('')} ` : parts.join('');
}

function tableLines(table: HTMLTableElement): string[] {
  const lines: string[] = [];
  const caption = table.querySelector('caption');
  lines.push(`[table] ${caption ? squash(inline(caption)) : '(no title)'}`);
  const rows = Array.from(table.querySelectorAll('tr'));
  rows.slice(0, TABLE_ROWS).forEach((tr) => {
    const cells = Array.from(tr.children).map((c) => squash(inline(c)));
    lines.push(`  | ${cells.join(' | ')} |`);
  });
  if (rows.length > TABLE_ROWS) lines.push(`  … ${rows.length - TABLE_ROWS} more rows`);
  return lines;
}

/** The document, block by block, as lines. */
function outline(el: Element, lines: string[]): void {
  if (el.tagName === 'TABLE') {
    lines.push(...tableLines(el as HTMLTableElement));
    return;
  }
  if (el.tagName.toLowerCase() === 'svg') {
    const label = el.getAttribute('aria-label');
    if (label) lines.push(`[chart] ${squash(label)}`);
    return;
  }
  if (HEADINGS.includes(el.tagName)) {
    lines.push(`${'#'.repeat(Number(el.tagName[1]))} ${squash(inline(el))}`);
    return;
  }
  if (el.tagName === 'FIGCAPTION') {
    lines.push(`[caption] ${squash(inline(el))}`);
    return;
  }
  const children = Array.from(el.childNodes);
  const hasBlockChild = children.some((c) => c instanceof Element && (isBlock(c) || c.querySelector('table, svg, h1, h2, h3, h4, h5, h6, p, div, li')));
  if (!hasBlockChild) {
    const text = squash(inline(el));
    if (text) lines.push(el.tagName === 'LI' ? `- ${text}` : text);
    return;
  }
  let run = '';
  const flush = () => {
    const text = squash(run);
    if (text) lines.push(text);
    run = '';
  };
  for (const child of children) {
    if (child instanceof Element && (isBlock(child) || child.querySelector('table, svg, h1, h2, h3, h4, h5, h6, p, div, li'))) {
      flush();
      outline(child, lines);
    } else {
      run += inline(child);
    }
  }
  flush();
}

/** Only the sections whose heading contains `section`: from such a heading to the next of the same or a higher level. */
function narrow(lines: string[], section: string): string[] {
  const q = section.toLocaleLowerCase();
  const kept: string[] = [];
  let level = 0;
  for (const line of lines) {
    const heading = /^(#+) /.exec(line);
    if (heading) {
      const depth = heading[1].length;
      if (level && depth <= level) level = 0;
      if (!level && line.toLocaleLowerCase().includes(q)) level = depth;
    }
    if (level) kept.push(line);
  }
  return kept;
}

/**
 * `values` are the report's own, as a fixture gives them; left out, its
 * defaults. `html` is the drawn document's markup, for the SDK kit's render.
 */
export async function previewOutline(compiled: CompiledReportBundle, context: RunReportContext | InputsReportContext, options: { section?: string; sample?: boolean; on?: string; values?: unknown } = {}): Promise<{ text: string; isError: boolean; html?: string }> {
  let module: LoadedReportModule;
  try {
    module = moduleFor(compiled);
  } catch (error) {
    return { text: `The compiled draft could not be loaded: ${error instanceof Error ? error.message : String(error)}`, isError: true };
  }
  const { definition } = module;
  const on = options.on ?? ('run' in context ? `on run "${context.run.title}"${options.sample ? ' (the sample run: no real run is loaded)' : ''}` : 'on its input sheet');
  const head = [`Report "${definition.title}" v${definition.version}, ${on}.`];

  let values: Record<string, unknown>;
  let model: { verdict: string; verdictLabel: string; findings: Array<{ severity: string; passed: boolean; message: string }> };
  try {
    values = definition.normalizeValues(options.values ?? definition.defaultValues());
    // the definition is erased to the run's context; a report filled in from inputs reads its own
    model = definition.evaluate(values, context as RunReportContext);
  } catch (error) {
    return { text: `${head[0]}\nEvaluating it threw: ${error instanceof Error ? error.message : String(error)}`, isError: true };
  }
  head.push(`Verdict: ${model.verdict} (prints "${model.verdictLabel}")`);
  const failing = model.findings.filter((f) => !f.passed);
  head.push(model.findings.length ? `Findings (${model.findings.length}, ${failing.length} not passed):` : 'Findings: none');
  for (const f of model.findings.slice(0, 60)) head.push(`- [${f.severity}${f.passed ? ', passed' : ''}] ${squash(f.message)}`);
  if (model.findings.length > 60) head.push(`- … ${model.findings.length - 60} more`);

  if (definition.traces && 'run' in context) {
    try {
      const asked = definition.traces(context.run) as RunTraceRequest[];
      const missing = asked.filter((r) => !context.traces[r.key]);
      if (missing.length) head.push(`Point files the report asks for that the preview does not hold (${missing.length} of ${asked.length}): ${missing.map((r) => r.key).join(', ')}${options.sample ? '. The sample run carries none; load a real run to see them drawn.' : '.'}`);
    } catch (error) {
      head.push(`Asking for point files threw: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  const drawn = await drawDocument(module, values, model, context as RunReportContext, (host) => {
    const lines: string[] = [];
    outline(host, lines);
    return { lines, html: host.innerHTML };
  });
  if (drawn.error) head.push(`Drawing the document threw: ${drawn.error}`);
  let body = drawn.value?.lines ?? [];
  if (options.section) {
    body = narrow(body, options.section);
    if (body.length === 0) body = [`No section heading contains "${options.section}".`];
  }
  let text = [...head, '', 'Document:', ...body].join('\n');
  if (text.length > OUTLINE_CHARS) text = `${text.slice(0, OUTLINE_CHARS)}\n… cut off at ${OUTLINE_CHARS} characters; preview with section narrows it.`;
  return { text, isError: drawn.error !== null, html: drawn.value?.html ?? '' };
}
